/**
 * First-touch marketing attribution for UpClinic (CRA).
 *
 * Captures UTM params plus gclid/fbclid from the URL on the first visit,
 * stores them in localStorage (uc_utm, 90-day TTL), and exposes helpers for
 * Firestore + GA4 / Meta Pixel signup conversion events (once per uid).
 */

const STORAGE_KEY = 'uc_utm';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days (aligned with landing)

const ATTR_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
];

const GA4_SIGN_UP_KEY_PREFIX = 'ga4_sign_up_fired_';

function canUseBrowserApis() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function isExpired(stored) {
  if (!stored || typeof stored.ts !== 'number') return true;
  return Date.now() - stored.ts > TTL_MS;
}

function parseStored() {
  if (!canUseBrowserApis()) return null;
  const raw = safeGet(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (isExpired(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function hasAnyAttr(obj) {
  return ATTR_KEYS.some((k) => obj[k] != null && String(obj[k]).trim() !== '');
}

/**
 * Read UTMs / click IDs from a query string (or current location).
 * First-touch only: does not overwrite a valid stored payload.
 *
 * @param {string} [search] - e.g. window.location.search
 * @returns {object|null} stored attribution after capture (or existing)
 */
export function captureAttributionFromUrl(search) {
  if (!canUseBrowserApis()) return null;

  const existing = parseStored();
  if (existing && hasAnyAttr(existing)) {
    return existing;
  }

  const query =
    typeof search === 'string'
      ? search
      : typeof window !== 'undefined'
        ? window.location.search
        : '';

  let params;
  try {
    params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  } catch {
    return existing;
  }

  const next = {
    ts: Date.now(),
    landing:
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '',
  };

  ATTR_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value != null && String(value).trim() !== '') {
      next[key] = String(value).trim();
    }
  });

  // Only persist on first touch when the URL carries marketing params.
  if (!hasAnyAttr(next)) {
    return existing;
  }

  // Align with landing: missing utm_content → sem_codigo
  if (!next.utm_content) {
    next.utm_content = 'sem_codigo';
  }

  safeSet(STORAGE_KEY, JSON.stringify(next));
  return next;
}

/**
 * @returns {object|null} valid first-touch attribution from localStorage
 */
export function getStoredAttribution() {
  return parseStored();
}

/**
 * Shape written to Firestore `users/{uid}.attribution`.
 * @returns {{
 *   utm_source?: string,
 *   utm_medium?: string,
 *   utm_campaign?: string,
 *   utm_content: string,
 *   utm_term?: string,
 *   gclid?: string,
 *   fbclid?: string,
 *   landing: string,
 *   ts: number
 * }}
 */
export function getAttributionForFirestore() {
  const stored = parseStored() || captureAttributionFromUrl();
  const base = stored && typeof stored === 'object' ? { ...stored } : {};

  const attribution = {
    utm_content:
      base.utm_content && String(base.utm_content).trim() !== ''
        ? String(base.utm_content).trim()
        : 'sem_codigo',
    landing:
      base.landing ||
      (typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : ''),
    ts: typeof base.ts === 'number' ? base.ts : Date.now(),
  };

  ATTR_KEYS.forEach((key) => {
    if (key === 'utm_content') return;
    if (base[key] != null && String(base[key]).trim() !== '') {
      attribution[key] = String(base[key]).trim();
    }
  });

  return attribution;
}

export function getUtmContent() {
  const attr = getAttributionForFirestore();
  return attr.utm_content || 'sem_codigo';
}

function hasFiredGa4SignUp(uid) {
  if (!uid || !canUseBrowserApis()) return false;
  return safeGet(`${GA4_SIGN_UP_KEY_PREFIX}${uid}`) === '1';
}

function markGa4SignUpFired(uid) {
  if (!uid || !canUseBrowserApis()) return;
  safeSet(`${GA4_SIGN_UP_KEY_PREFIX}${uid}`, '1');
}

/**
 * Fire GA4 `sign_up` once per uid (gtag → G-K7SE125H5D via index.html).
 * @param {string} uid
 * @param {string} [method='email']
 */
export function trackGa4SignUp(uid, method = 'email') {
  if (!canUseBrowserApis()) return;
  if (uid && hasFiredGa4SignUp(uid)) return;

  const utm_content = getUtmContent();
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'sign_up', {
        method: method || 'email',
        utm_content,
      });
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'sign_up',
        method: method || 'email',
        utm_content,
      });
    }
  } catch {
    /* never break auth */
  }

  if (uid) markGa4SignUpFired(uid);
}

/**
 * Run capture as early as possible (call from index.tsx).
 */
export function initAttribution() {
  return captureAttributionFromUrl();
}
